import { useEffect, useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Check, ChevronDown, X, Search } from 'lucide-react';
import {
  parsePhoneNumber,
  isValidPhoneNumber,
  CountryCode,
  getCountryCallingCode,
  AsYouType,
} from 'libphonenumber-js';
import { countries, Country, getCountryByCode, detectUserCountry, getPhoneValidationInfo } from '@/lib/phone-utils';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  countryCode: string;
  onCountryChange: (code: string) => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  showValidation?: boolean;
}

export function PhoneInput({
  value,
  onChange,
  countryCode,
  onCountryChange,
  label = 'Phone Number',
  required = false,
  placeholder = 'Enter phone number',
  showValidation = true,
}: PhoneInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const selectedCountry = getCountryByCode(countryCode) || countries[0];
  
  // Auto-detect country on mount
  useEffect(() => {
    if (!countryCode) {
      const detected = detectUserCountry();
      onCountryChange(detected);
    }
  }, []);

  // Validation state
  const validationInfo = getPhoneValidationInfo(value, countryCode as CountryCode);
  const isValid = value.length > 0 && validationInfo.isValid;
  const showError = value.length > 0 && !validationInfo.isValid && !isFocused;

  // Filter countries
  const filteredCountries = countries.filter((country) =>
    country.name.toLowerCase().includes(search.toLowerCase()) ||
    country.code.toLowerCase().includes(search.toLowerCase()) ||
    country.dialCode.includes(search)
  );

  // Format phone number as user types
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');
    
    try {
      const formatter = new AsYouType(countryCode as CountryCode);
      const formatted = formatter.input(rawValue);
      onChange(rawValue);
    } catch {
      onChange(rawValue);
    }
  };

  // Get formatted display value
  const getDisplayValue = () => {
    if (!value) return '';
    try {
      const formatter = new AsYouType(countryCode as CountryCode);
      return formatter.input(value);
    } catch {
      return value;
    }
  };

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  const handleCountrySelect = (country: Country) => {
    onCountryChange(country.code);
    setOpen(false);
    setSearch('');
    inputRef.current?.focus();
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      
      <div className="flex gap-2">
        {/* Country Selector */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-[120px] justify-between px-2 shrink-0"
            >
              <span className="flex items-center gap-1.5 truncate">
                <span className="text-lg">{selectedCountry.flag}</span>
                <span className="text-sm font-medium">{selectedCountry.dialCode}</span>
              </span>
              <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0 z-50" align="start">
            {/* Search */}
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search countries..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9"
                />
              </div>
            </div>
            
            {/* Country List */}
            <ScrollArea className="h-[250px]">
              <div className="p-1">
                {filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    onClick={() => handleCountrySelect(country)}
                    className={cn(
                      "w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm hover:bg-accent transition-colors",
                      country.code === countryCode && "bg-accent"
                    )}
                  >
                    <span className="text-lg w-6">{country.flag}</span>
                    <span className="flex-1 text-left truncate">{country.name}</span>
                    <span className="text-muted-foreground">{country.dialCode}</span>
                    {country.code === countryCode && (
                      <Check className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </button>
                ))}
                {filteredCountries.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No countries found
                  </p>
                )}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>

        {/* Phone Input */}
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            type="tel"
            value={getDisplayValue()}
            onChange={handlePhoneChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            className={cn(
              "pr-10",
              showValidation && isValid && "border-primary focus-visible:ring-primary",
              showValidation && showError && "border-destructive focus-visible:ring-destructive"
            )}
          />
          
          {/* Clear button & Validation indicator */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {value && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-accent rounded-full transition-colors"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
            {showValidation && value && (
              <div className={cn(
                "w-5 h-5 rounded-full flex items-center justify-center",
                isValid ? "bg-primary/10" : "bg-destructive/10"
              )}>
                {isValid ? (
                  <Check className="h-3 w-3 text-primary" />
                ) : (
                  <X className="h-3 w-3 text-destructive" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Validation message */}
      {showValidation && showError && validationInfo.message && (
        <p className="text-sm text-destructive">{validationInfo.message}</p>
      )}
      
      {showValidation && isValid && (
        <p className="text-sm text-primary">
          ✓ Valid {selectedCountry.name} number
        </p>
      )}
    </div>
  );
}

// Helper to get full phone number with country code
export function getFullPhoneNumber(phone: string, countryCode: string): string {
  if (!phone) return '';
  try {
    const dialCode = getCountryCallingCode(countryCode as CountryCode);
    return `${dialCode}${phone}`;
  } catch {
    return phone;
  }
}
